import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { Analysis, FormValues, AnalysisResultItem } from '@/lib/types';
import { BookOpenText, ChevronDown, ChevronUp } from 'lucide-react';
import {
  computeOverallDisplay,
  computeSectionSummaries,
  directionClass,
  biasToDir,
  formatDelta
} from './lib/derivations';
import type { OverallDisplay, SectionSummaries } from './lib/derivations';
import { postSopFooter } from '@/lib/evaluation-api-client';
import { useSettings } from '@/components/settings/lib/settings-context';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';
import logger from '@/lib/logging';
import { computeCollapsedTokensFromSop } from './lib/classification';

type Variant = 'sop' | 'posture';

export interface AnalysisFooterDisplayProps {
  variant: Variant;
  // Generic progress/counts
  leftCount?: number;
  rightCount?: number;
  intactCount?: number;
  totalTests?: number;
  completedTests?: number;
  // Shared signal inputs
  formValues?: FormValues; // Required for SOP layout
  analysis?: Analysis | null;
  clientClassificationCode?: string;
  // Options
  showRuleDocsBadge?: boolean;
  phaseGoals?: any;
  // Collapsed controls
  createdAt?: Date | null;
  onChangeCreatedAt?: (d: Date) => void;
  priorAssessments?: { id: string; label: string; createdAt?: Date | null }[];
  onSelectAssessment?: (id: string) => void;
  // Visibility toggle
  isVisible?: boolean;
  onToggleVisibility?: (visible: boolean) => void;
  defaultVisible?: boolean;
}

export const AnalysisFooterDisplay: React.FC<AnalysisFooterDisplayProps> = ({
  variant,
  leftCount = 0,
  rightCount = 0,
  intactCount = 0,
  totalTests = 0,
  completedTests = 0,
  formValues,
  analysis,
  clientClassificationCode,
  showRuleDocsBadge = true,
  phaseGoals,
  createdAt,
  onChangeCreatedAt,
  priorAssessments,
  onSelectAssessment,
  isVisible: controlledVisible,
  onToggleVisibility,
  defaultVisible = true,
}) => {
  const [internalVisible, setInternalVisible] = useState(defaultVisible);
  const [isExpanded, setIsExpanded] = useState(false);
  const { settings } = useSettings();
  const ruleDocVersion: RuleDocVersion = settings.useLegacySopAssessment ? 'legacy' : 'current';
  const [serverOverall, setServerOverall] = useState<OverallDisplay | null>(null);
  const [serverSections, setServerSections] = useState<SectionSummaries | null>(null);

  // Use controlled visibility if provided, otherwise use internal state
  const isVisible = controlledVisible !== undefined ? controlledVisible : internalVisible;
  const setIsVisible = (visible: boolean) => {
    if (controlledVisible === undefined) {
      setInternalVisible(visible);
    }
    onToggleVisibility?.(visible);
  };

  const progressWidth = completedTests > 0 ? (completedTests / (totalTests || 1)) * 100 : 0;

  useEffect(() => {
    if (variant !== 'sop' || !formValues) {
      setServerOverall(null);
      setServerSections(null);
      return;
    }
    let cancelled = false;
    const ac = new AbortController();
    const tid = setTimeout(() => {
      postSopFooter(
        {
          formValues,
          analysis: analysis ?? null,
          clientClassificationCode: clientClassificationCode ?? null,
          ruleDocVersion,
          phaseGoals: phaseGoals ?? null,
          variant: 'sop'
        },
        { signal: ac.signal }
      )
        .then((res) => {
          if (!cancelled) {
            setServerOverall(res.overallDisplay as OverallDisplay);
            setServerSections(res.sectionSummaries as SectionSummaries);
          }
        })
        .catch((err: unknown) => {
          if (cancelled || (err as Error)?.name === 'AbortError') return;
          logger.error('AnalysisFooterDisplay: sop-footer failed', err);
        });
    }, 220);
    return () => {
      cancelled = true;
      ac.abort();
      clearTimeout(tid);
    };
  }, [variant, formValues, analysis, clientClassificationCode, ruleDocVersion, phaseGoals]);

  const overall = useMemo((): OverallDisplay => {
    if (variant === 'sop' && serverOverall) return serverOverall;
    return computeOverallDisplay(
      formValues,
      analysis ?? null,
      clientClassificationCode,
      'P1',
      { suppressBalanced: variant === 'sop' }
    );
  }, [variant, serverOverall, formValues, analysis, clientClassificationCode]);

  const sections = useMemo((): SectionSummaries | null => {
    if (variant === 'sop' && serverSections) return serverSections;
    return formValues ? computeSectionSummaries(formValues, variant === 'sop' ? phaseGoals : undefined) : null;
  }, [variant, serverSections, formValues, phaseGoals]);

  const leftPercentage = completedTests > 0 ? Math.round((leftCount / completedTests) * 100) : 0;
  const rightPercentage = completedTests > 0 ? Math.round((rightCount / completedTests) * 100) : 0;
  const intactPercentage = completedTests > 0 ? Math.round((intactCount / completedTests) * 100) : 0;

  const tokens = variant === 'sop' && formValues ? computeCollapsedTokensFromSop(formValues, analysis ?? null, phaseGoals) : undefined;

  if (!isVisible) {
    return null;
  }

  return (
    <Card className="border-2">
      <CardHeader className="pb-3">
        <Button
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 h-auto hover:bg-muted/50 whitespace-normal"
        >
          <div className="flex-1 flex items-center gap-3 flex-wrap text-left min-w-0 pr-2 overflow-hidden">
            <h3 className="text-lg font-extrabold tracking-tight">Analysis</h3>
            {showRuleDocsBadge && (
              <Badge variant="secondary" className="text-[10px] font-semibold">
                <BookOpenText className="h-3 w-3 mr-1 inline" /> RuleDocs
              </Badge>
            )}
            {!!completedTests && (
              <Badge variant="outline" className="text-xs font-semibold">
                {completedTests}/{totalTests} tests
              </Badge>
            )}
            {!isExpanded && (
              <Badge
                variant="outline"
                className={cn(
                  'text-xs font-bold whitespace-nowrap max-w-full',
                  overall.lateralizationLabel === 'LLat' && 'border-primary text-primary',
                  overall.lateralizationLabel === 'RLat' && 'border-error text-error-text',
                  overall.lateralizationLabel === 'Balanced' && 'border-success text-success-text',
                  overall.lateralizationLabel === 'Mixed' && 'border-warning text-warning-text'
                )}
                title={tokens ? (
                  variant === 'sop'
                    ? `${tokens.lateralized ? `${tokens.lateralized} Lat` : 'SOP'}${tokens.phase ? `, ${tokens.phase}` : ''}`
                    : `${tokens.pattern === 'SB' ? 'Swayback' : tokens.pattern === 'APT' ? 'APT' : 'Neutral'}${tokens.lateralized ? `, ${tokens.lateralized} Lat` : ''}`
                ) : overall.fullText}
              >
                <span className="truncate">
                  {tokens ? (
                    variant === 'sop'
                      ? `${tokens.lateralized ? `${tokens.lateralized} Lat` : 'SOP'}${tokens.phase ? `, ${tokens.phase}` : ''}`
                      : `${tokens.pattern === 'SB' ? 'Swayback' : tokens.pattern === 'APT' ? 'APT' : 'Neutral'}${tokens.lateralized ? `, ${tokens.lateralized} Lat` : ''}`
                  ) : overall.fullText}
                </span>
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isExpanded ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </Button>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          {!!completedTests && (
            <div className="mb-4">
              <div className="h-2 bg-muted-foreground/10 rounded">
                <div className="h-2 rounded bg-primary transition-all" style={{ width: `${progressWidth}%` }} />
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground">
                L {leftPercentage}% • R {rightPercentage}%{variant !== 'sop' && intactCount ? ` • I ${intactPercentage}%` : ''}
              </div>
            </div>
          )}

          {/* Full classification summary when expanded */}
          <div className="mb-4 p-3 bg-muted/50 rounded-lg">
            <div className="text-sm font-semibold">
              {tokens ? (
                variant === 'sop'
                  ? `${tokens.lateralized ? `${tokens.lateralized} Lateralized` : 'Lateralization Unavailable'}${tokens.grounded ? `, ${tokens.grounded} Grounded` : ''}${tokens.rightFoot ? `, Right Foot ${tokens.rightFoot}` : ''}${tokens.leftFoot ? `, Left Foot ${tokens.leftFoot}` : ''}${tokens.phase ? `, ${tokens.phase}` : ''}`
                  : `${tokens.pattern === 'SB' ? 'Swayback Posture' : tokens.pattern === 'APT' ? 'Anteriorly Oriented Posture' : 'Neutral'}${tokens.lateralized ? `, ${tokens.lateralized} Lateralized` : ''}${tokens.grounded ? `, ${tokens.grounded} Grounded` : ''}${tokens.rightFoot ? `, Right Foot ${tokens.rightFoot}` : ''}${tokens.leftFoot ? `, Left Foot ${tokens.leftFoot}` : ''}${tokens.phase ? `, ${tokens.phase}` : ''}`
              ) : overall.fullText}
            </div>
          </div>

          {/* Analysis Results - Left/Right/Balanced breakdown */}
          {analysis && (
            <div className="mb-6">
              <h3 className="text-base font-semibold mb-4">Analysis Results</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Left-Lateralized */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-primary">Left-Lateralized</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="space-y-2">
                      {analysis.left.length > 0 ? (
                        analysis.left.map((item: AnalysisResultItem, index: number) => {
                          const superscripts = `${item.isLeftHyper ? '↑L' : ''}${item.isRightHyper ? '↑R' : ''}`;
                          return (
                            <li 
                              key={`${item.test}-${index}`} 
                              className={cn(
                                "text-sm px-3 py-1.5 rounded-md",
                                item.isFunctionalOccupancy ? 'bg-success text-success-foreground/20' : 'bg-error text-error-foreground/20'
                              )}
                            >
                              {item.test} {superscripts && <sup className="text-warning-text font-bold">{superscripts}</sup>}
                            </li>
                          );
                        })
                      ) : (
                        <li className="text-muted-foreground text-center text-sm italic">None</li>
                      )}
                    </ul>
                  </CardContent>
                </Card>

                {/* Balanced */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-success-text">Balanced</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="space-y-2">
                      {analysis.intact.length > 0 ? (
                        analysis.intact.map((item: AnalysisResultItem, index: number) => {
                          const superscripts = `${item.isLeftHyper ? '↑L' : ''}${item.isRightHyper ? '↑R' : ''}`;
                          return (
                            <li 
                              key={`${item.test}-${index}`} 
                              className={cn(
                                "text-sm px-3 py-1.5 rounded-md",
                                item.isFunctionalOccupancy ? 'bg-success text-success-foreground/20' : 'bg-error text-error-foreground/20'
                              )}
                            >
                              {item.test} {superscripts && <sup className="text-warning-text font-bold">{superscripts}</sup>}
                            </li>
                          );
                        })
                      ) : (
                        <li className="text-muted-foreground text-center text-sm italic">None</li>
                      )}
                    </ul>
                  </CardContent>
                </Card>

                {/* Right-Lateralized */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-error-text">Right-Lateralized</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="space-y-2">
                      {analysis.right.length > 0 ? (
                        analysis.right.map((item: AnalysisResultItem, index: number) => {
                          const superscripts = `${item.isLeftHyper ? '↑L' : ''}${item.isRightHyper ? '↑R' : ''}`;
                          return (
                            <li 
                              key={`${item.test}-${index}`} 
                              className={cn(
                                "text-sm px-3 py-1.5 rounded-md",
                                item.isFunctionalOccupancy ? 'bg-success text-success-foreground/20' : 'bg-error text-error-foreground/20'
                              )}
                            >
                              {item.test} {superscripts && <sup className="text-warning-text font-bold">{superscripts}</sup>}
                            </li>
                          );
                        })
                      ) : (
                        <li className="text-muted-foreground text-center text-sm italic">None</li>
                      )}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Qualitative Analysis */}
          {formValues && (
            <div className="mb-6">
              <h3 className="text-base font-semibold mb-4">Qualitative Analysis</h3>
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Grounding (L)</h4>
                      <p className="text-2xl font-mono">{formValues.L_GroundingPerformance || 'N/A'}</p>
                    </div>
                    <div className="md:border-x px-4">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Foot Presentation</h4>
                      <p className="text-2xl font-semibold text-primary">{formValues.FootPresentation || 'N/A'}</p>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Grounding (R)</h4>
                      <p className="text-2xl font-mono">{formValues.R_GroundingPerformance || 'N/A'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Body Diagram */}
          {variant === 'sop' && sections && (
            <div className="w-full flex items-center justify-center">
              <div className="[--box:theme(colors.cyan.400)] [--bg:theme(colors.slate.900/.3)] flex flex-col items-center gap-3">
                {/* Head */}
                <Shell className="w-24 h-16">
                  <div className="flex flex-col items-center justify-center leading-none">
                    <span className="text-[9px] tracking-wide text-muted-foreground">LATERALIZATION</span>
                    <ArrowIcon dir={sections.cervical.intact ? 'Both' : sections.cervical.dir} color={sections.cervical.dir === 'Left' ? 'blue' : sections.cervical.dir === 'Right' ? 'red' : 'blue'} size={56} />
                  </div>
                  {sections.cervical.hyper && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-warning text-warning-foreground" />}
                </Shell>

                {/* Shoulder/torso row */}
                <div className="flex items-center justify-center gap-3">
                  <Shell className="w-16 h-40">
                    <div className="flex flex-col items-center justify-center leading-none">
                      <span className="text-[9px] tracking-wide text-muted-foreground">BIAS</span>
                      {/* Arms bias: outward for ER, inward for IR; neutral color smaller */}
                      <ArrowIcon dir={sections.armsTorso.leftArm === 'ER bias' ? 'Left' : sections.armsTorso.leftArm === 'IR bias' ? 'Right' : 'Both'} color="neutral" size={32} />
                    </div>
                  </Shell>
                  <Shell className="w-48 h-28">
                    <div className="flex flex-col items-center justify-center leading-none">
                      <span className="text-[9px] tracking-wide text-muted-foreground">LATERALIZATION</span>
                      <ArrowIcon dir={sections.ribcage.intact ? 'Both' : sections.ribcage.dir} color={sections.ribcage.dir === 'Left' ? 'blue' : sections.ribcage.dir === 'Right' ? 'red' : 'blue'} size={56} />
                    </div>
                    {sections.ribcage.hyper && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-warning text-warning-foreground" />}
                  </Shell>
                  <Shell className="w-16 h-40">
                    <div className="flex flex-col items-center justify-center leading-none">
                      <span className="text-[9px] tracking-wide text-muted-foreground">BIAS</span>
                      <ArrowIcon dir={sections.armsTorso.rightArm === 'ER bias' ? 'Right' : sections.armsTorso.rightArm === 'IR bias' ? 'Left' : 'Both'} color="neutral" size={32} />
                    </div>
                  </Shell>
                </div>

                {/* Pelvis */}
                <Shell className="w-28 h-16">
                  <div className="flex flex-col items-center justify-center leading-none">
                    <span className="text-[9px] tracking-wide text-muted-foreground">LATERALIZATION</span>
                    <ArrowIcon dir={sections.pelvis.intact ? 'Both' : sections.pelvis.dir} color={sections.pelvis.dir === 'Left' ? 'blue' : sections.pelvis.dir === 'Right' ? 'red' : 'blue'} size={56} />
                  </div>
                  {sections.pelvis.hyper && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-warning text-warning-foreground" />}
                </Shell>

                {/* Legs */}
                <div className="flex items-center justify-center gap-3">
                  <Shell className="w-20 h-44">
                    <div className="flex flex-col items-center justify-center leading-none">
                      <span className="text-[9px] tracking-wide text-muted-foreground">BIAS</span>
                      <ArrowIcon dir={sections.legsPerSide.leftLeg === 'ER bias' ? 'Left' : sections.legsPerSide.leftLeg === 'IR bias' ? 'Right' : 'Both'} color="neutral" size={32} />
                    </div>
                    {sections.legsPerSide.leftHyper && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-warning text-warning-foreground" />}
                  </Shell>
                  <Shell className="w-20 h-44">
                    <div className="flex flex-col items-center justify-center leading-none">
                      <span className="text-[9px] tracking-wide text-muted-foreground">BIAS</span>
                      <ArrowIcon dir={sections.legsPerSide.rightLeg === 'ER bias' ? 'Right' : sections.legsPerSide.rightLeg === 'IR bias' ? 'Left' : 'Both'} color="neutral" size={32} />
                    </div>
                    {sections.legsPerSide.rightHyper && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-warning text-warning-foreground" />}
                  </Shell>
                </div>

                {/* Feet */}
                <div className="flex items-center justify-center gap-3">
                  {(() => {
                    const ls = sections.footGround.leftScore;
                    const rs = sections.footGround.rightScore;
                    const leftIsMore = (ls ?? -Infinity) >= (rs ?? -Infinity);
                    // Left foot: green inward (Right) if more grounded; else red outward (Left)
                    const leftColor = leftIsMore ? 'green' : 'red';
                    const leftDir = leftIsMore ? 'Right' : 'Left';
                    // Right foot: green inward (Left) if more grounded; else red outward (Right)
                    const rightColor = leftIsMore ? 'red' : 'green';
                    const rightDir = leftIsMore ? 'Right' /* outward for less grounded right */ : 'Left'; /* inward for more grounded right */
                    return (
                      <>
                        <Shell className="w-16 h-16">
                          <div className="relative flex items-center justify-center">
                            <ArrowIcon dir={leftDir as any} color={leftColor as any} size={40} />
                            {ls !== null && (
                              <span className="absolute text-[10px] font-bold text-accent-fg">{Math.round(ls as number)}</span>
                            )}
                          </div>
                        </Shell>
                        <Shell className="w-16 h-16">
                          <div className="relative flex items-center justify-center">
                            <ArrowIcon dir={rightDir as any} color={rightColor as any} size={40} />
                            {rs !== null && (
                              <span className="absolute text-[10px] font-bold text-accent-fg">{Math.round(rs as number)}</span>
                            )}
                          </div>
                        </Shell>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {variant === 'posture' && (
            <div className="mt-2 text-xs text-muted-foreground">
              <span>Pattern: {overall.patternLabel ?? '—'}</span>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default AnalysisFooterDisplay;

// helpers
function Shell({ children, className }: { children: React.ReactNode, className?: string }) {
  return (
    <div className={cn('rounded-2xl border shadow-[inset_0_0_0_2px_rgba(0,0,0,0.2)]', 'border-[var(--box)] bg-[var(--bg)]', 'flex items-center justify-center', className)}>
      {children}
    </div>
  );
}

function ArrowIcon({ dir, color, size = 56 }: { dir: 'Left' | 'Right' | 'Both', color: 'red' | 'green' | 'blue' | 'neutral', size?: number }) {
  const fill = color === 'red' ? '#ef4444' : color === 'green' ? '#10b981' : color === 'blue' ? 'hsl(var(--primary))' : '#94a3b8';
  if (dir === 'Both') {
    return (
      <div className="flex items-center gap-2">
        <svg width={size} height={size} viewBox="0 0 24 24">
          <polygon points="16,4 6,12 16,20" fill={fill} opacity={color === 'neutral' ? 0.3 : 0.9} />
        </svg>
        <svg width={size} height={size} viewBox="0 0 24 24">
          <polygon points="8,4 18,12 8,20" fill={fill} opacity={color === 'neutral' ? 0.3 : 0.9} />
        </svg>
      </div>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24">
      {dir === 'Left' ? (
        <polygon points="16,4 6,12 16,20" fill={fill} opacity={color === 'neutral' ? 0.3 : 0.9} />
      ) : (
        <polygon points="8,4 18,12 8,20" fill={fill} opacity={color === 'neutral' ? 0.3 : 0.9} />
      )}
    </svg>
  );
}


